// src/pages/documentation/gestion/GestionClientes.jsx

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUsers,
    faPlus,
    faEdit,
    faSearch,
    faChartBar,
    faHistory,
    faQrcode,
    faFileImport,
    faGift,
    faStar,
    faArrowRight,
    faArrowLeft,
    faCheckCircle,
    faInfoCircle,
    faExclamationTriangle,
    faLightbulb,
    faFilter
} from '@fortawesome/free-solid-svg-icons';
import DocumentationLayout from '../../../components/documentation/DocumentationLayout';

const GestionClientes = () => {
    const [expandedSections, setExpandedSections] = useState({
        introduccion: true,
        verClientes: true,
        crearCliente: true,
        gestionarPuntos: true,
        segmentacion: true,
        historial: true
    });

    const toggleSection = (sectionId) => {
        setExpandedSections(prev => ({
            ...prev,
            [sectionId]: !prev[sectionId]
        }));
    };

    return (
        <DocumentationLayout>
            <div className="p-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center gap-3 mb-4">
                        <div className="p-3 bg-green-600 rounded-lg">
                            <FontAwesomeIcon icon={faUsers} className="text-white text-2xl" />
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold text-gray-800">Gestión de Clientes</h1>
                            <p className="text-gray-600">Aprende a administrar tu base de clientes y sus puntos</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Link to="/documentation" className="hover:text-gray-700">Centro de Ayuda</Link>
                        <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
                        <Link to="/documentation/gestion" className="hover:text-gray-700">Gestión</Link>
                        <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
                        <span className="text-gray-700">Clientes</span>
                    </div>
                </div>

                {/* Introducción */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6 mb-8">
                    <div className="flex items-start gap-4">
                        <FontAwesomeIcon icon={faInfoCircle} className="text-green-600 text-xl mt-1" />
                        <div>
                            <h3 className="font-semibold text-gray-900 mb-2">¿Qué es la Gestión de Clientes?</h3>
                            <p className="text-gray-700 mb-3">
                                La gestión de clientes te permite administrar toda tu base de clientes registrados en PetitPrime. 
                                Desde aquí puedes ver información detallada de cada cliente, gestionar sus puntos, ver su historial de compras, 
                                crear segmentos para campañas personalizadas y mucho más.
                            </p>
                            <div className="grid md:grid-cols-3 gap-4 mt-4">
                                <div className="bg-white rounded-lg p-4 border border-green-100">
                                    <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faUsers} className="text-green-600" />
                                        Base de Clientes
                                    </h4>
                                    <p className="text-sm text-gray-600">
                                        Visualiza y gestiona todos tus clientes desde un solo lugar
                                    </p>
                                </div>
                                <div className="bg-white rounded-lg p-4 border border-green-100">
                                    <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faGift} className="text-green-600" />
                                        Puntos y Premios
                                    </h4>
                                    <p className="text-sm text-gray-600">
                                        Administra los puntos de tus clientes y gestiona canjes de premios
                                    </p>
                                </div>
                                <div className="bg-white rounded-lg p-4 border border-green-100">
                                    <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faChartBar} className="text-green-600" />
                                        Segmentación
                                    </h4>
                                    <p className="text-sm text-gray-600">
                                        Crea segmentos inteligentes para campañas personalizadas
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Sección: Ver y Buscar Clientes */}
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-6">
                    <button
                        onClick={() => toggleSection('verClientes')}
                        className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                        <div className="flex items-center gap-4">
                            <div className="flex-shrink-0 w-10 h-10 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">
                                <FontAwesomeIcon icon={faUsers} />
                            </div>
                            <div>
                                <h2 className="text-xl font-semibold text-gray-800">Ver y Buscar Clientes</h2>
                                <p className="text-sm text-gray-500">Accede a tu lista de clientes y busca información específica</p>
                            </div>
                        </div>
                        <FontAwesomeIcon
                            icon={faArrowRight}
                            className={`text-gray-400 transform transition-transform ${expandedSections.verClientes ? 'rotate-90' : ''}`}
                        />
                    </button>

                    {expandedSections.verClientes && (
                        <div className="px-6 pb-6 border-t border-gray-100 bg-gray-50">
                            <div className="pt-6 space-y-6">
                                {/* Acceder a la lista */}
                                <div className="bg-white border border-gray-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faUsers} className="text-green-600" />
                                        1. Acceder a la Lista de Clientes
                                    </h3>
                                    <div className="space-y-4">
                                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-green-800 mb-2">📍 ¿Dónde ver los clientes?</h4>
                                            <p className="text-green-700 text-sm mb-3">
                                                Puedes acceder a la lista de clientes de dos formas:
                                            </p>
                                            <ul className="text-green-700 text-sm space-y-2">
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Desde Gestión de Marcas y Tiendas:</strong> Haz clic en "Ver Clientes" en la tarjeta de una tienda específica
                                                    </div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Desde el menú principal:</strong> Accede directamente a la sección de "Clientes" desde tu dashboard
                                                    </div>
                                                </li>
                                            </ul>
                                        </div>

                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-blue-800 mb-2">Información que verás:</h4>
                                            <ul className="text-blue-700 text-sm space-y-2">
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>Nombre completo y datos de contacto (email, teléfono)</div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>Puntos actuales del cliente</div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>Fecha de registro</div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>Última compra</div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>Acciones rápidas (editar, acumular puntos, canjear premios)</div>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>

                                {/* Buscar Clientes */}
                                <div className="bg-white border border-gray-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faSearch} className="text-green-600" />
                                        2. Buscar Clientes
                                    </h3>
                                    <div className="space-y-4">
                                        <p className="text-gray-600">
                                            La lista de clientes incluye una barra de búsqueda que te permite encontrar clientes rápidamente.
                                        </p>
                                        
                                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-green-800 mb-2">Cómo buscar:</h4>
                                            <ul className="text-green-700 text-sm space-y-2">
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>Escribe en el campo de búsqueda cualquier término (nombre, email, teléfono)</div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>La búsqueda se realiza en tiempo real mientras escribes</div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>Los resultados se filtran automáticamente mostrando solo los clientes que coinciden</div>
                                                </li>
                                            </ul>
                                        </div>

                                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-yellow-800 mb-2 flex items-center gap-2">
                                                <FontAwesomeIcon icon={faLightbulb} />
                                                Tip
                                            </h4>
                                            <p className="text-yellow-700 text-sm">
                                                Puedes buscar por nombre completo, solo el nombre, solo el apellido, email o número de teléfono. 
                                                La búsqueda es flexible y encuentra coincidencias parciales.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Paginación */}
                                <div className="bg-white border border-gray-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faFilter} className="text-green-600" />
                                        3. Navegar entre Páginas
                                    </h3>
                                    <div className="space-y-4">
                                        <p className="text-gray-600">
                                            Si tienes muchos clientes, la lista se divide en páginas para facilitar la navegación.
                                        </p>
                                        
                                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-green-800 mb-2">Paginación:</h4>
                                            <ul className="text-green-700 text-sm space-y-2">
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>Por defecto se muestran 10 clientes por página</div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>Usa los botones de navegación en la parte inferior para cambiar de página</div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>Puedes ver el número total de clientes y en qué página estás</div>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Sección: Crear Cliente */}
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-6">
                    <button
                        onClick={() => toggleSection('crearCliente')}
                        className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                        <div className="flex items-center gap-4">
                            <div className="flex-shrink-0 w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                                <FontAwesomeIcon icon={faPlus} />
                            </div>
                            <div>
                                <h2 className="text-xl font-semibold text-gray-800">Crear Cliente Manualmente</h2>
                                <p className="text-sm text-gray-500">Registra nuevos clientes en tu sistema</p>
                            </div>
                        </div>
                        <FontAwesomeIcon
                            icon={faArrowRight}
                            className={`text-gray-400 transform transition-transform ${expandedSections.crearCliente ? 'rotate-90' : ''}`}
                        />
                    </button>

                    {expandedSections.crearCliente && (
                        <div className="px-6 pb-6 border-t border-gray-100 bg-gray-50">
                            <div className="pt-6 space-y-6">
                                {/* Crear Cliente */}
                                <div className="bg-white border border-gray-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faPlus} className="text-blue-600" />
                                        1. Crear un Nuevo Cliente
                                    </h3>
                                    <div className="space-y-4">
                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-blue-800 mb-2">📍 ¿Dónde crear un cliente?</h4>
                                            <p className="text-blue-700 text-sm mb-3">
                                                Puedes crear un cliente desde la página de gestión de tiendas haciendo clic en <strong>"Crear Cliente"</strong> 
                                                en la tarjeta de una tienda específica.
                                            </p>
                                        </div>

                                        <div className="space-y-3">
                                            <h4 className="font-semibold text-gray-700">Información requerida:</h4>
                                            <ul className="space-y-2 text-sm text-gray-600">
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Nombre y Apellido:</strong> Nombre completo del cliente
                                                    </div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Email o Teléfono:</strong> Al menos uno de los dos es obligatorio para poder contactar al cliente
                                                    </div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Fecha de Cumpleaños:</strong> Opcional, pero útil para enviar ofertas especiales
                                                    </div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Género:</strong> Opcional, puedes elegir "Prefiero no decirlo"
                                                    </div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Información Geográfica:</strong> Opcional (país, estado, ciudad, código postal)
                                                    </div>
                                                </li>
                                            </ul>
                                        </div>

                                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-yellow-800 mb-2 flex items-center gap-2">
                                                <FontAwesomeIcon icon={faLightbulb} />
                                                Tip
                                            </h4>
                                            <p className="text-yellow-700 text-sm">
                                                Mientras más información proporciones del cliente, mejor podrás segmentarlo y personalizar sus experiencias. 
                                                Sin embargo, solo el email o teléfono son obligatorios.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Código de Referido */}
                                <div className="bg-white border border-gray-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faStar} className="text-blue-600" />
                                        2. Código de Referido
                                    </h3>
                                    <div className="space-y-4">
                                        <p className="text-gray-600">
                                            Si el cliente fue referido por otro cliente existente, puedes ingresar el código de referido durante el registro.
                                        </p>
                                        
                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-blue-800 mb-2">Cómo usar códigos de referido:</h4>
                                            <ol className="text-blue-700 text-sm space-y-2 list-decimal list-inside">
                                                <li>Si el cliente tiene un código de referido, ingrésalo en el campo correspondiente</li>
                                                <li>El sistema validará automáticamente que el código pertenezca a un cliente de tu marca</li>
                                                <li>Si el código es válido, se vinculará al cliente referente</li>
                                                <li>Esto te permite hacer seguimiento de tus programas de referidos</li>
                                            </ol>
                                        </div>

                                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-green-800 mb-2">✅ Beneficios:</h4>
                                            <ul className="text-green-700 text-sm space-y-1">
                                                <li>• Puedes rastrear qué clientes trajeron nuevos clientes</li>
                                                <li>• Útil para programas de fidelización y referidos</li>
                                                <li>• Te ayuda a identificar a tus mejores embajadores de marca</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>

                                {/* Importar Clientes */}
                                <div className="bg-white border border-gray-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faFileImport} className="text-blue-600" />
                                        3. Importar Clientes Masivamente
                                    </h3>
                                    <div className="space-y-4">
                                        <p className="text-gray-600">
                                            Si tienes muchos clientes para registrar, puedes importarlos desde un archivo Excel o CSV.
                                        </p>
                                        
                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-blue-800 mb-2">Cómo importar:</h4>
                                            <ol className="text-blue-700 text-sm space-y-2 list-decimal list-inside">
                                                <li>Accede a la página de "Importar Clientes" desde el menú</li>
                                                <li>Prepara un archivo Excel o CSV con las columnas: nombre, apellido, email, teléfono</li>
                                                <li>Sube el archivo usando el botón de carga</li>
                                                <li>Revisa la vista previa de los datos antes de confirmar</li>
                                                <li>Confirma la importación</li>
                                            </ol>
                                        </div>

                                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-yellow-800 mb-2">⚠️ Importante:</h4>
                                            <ul className="text-yellow-700 text-sm space-y-1">
                                                <li>• Asegúrate de que el formato del archivo sea correcto</li>
                                                <li>• Los emails deben ser únicos (no pueden estar duplicados)</li>
                                                <li>• Revisa los datos antes de importar para evitar errores</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Sección: Gestionar Puntos */}
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-6">
                    <button
                        onClick={() => toggleSection('gestionarPuntos')}
                        className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                        <div className="flex items-center gap-4">
                            <div className="flex-shrink-0 w-10 h-10 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">
                                <FontAwesomeIcon icon={faGift} />
                            </div>
                            <div>
                                <h2 className="text-xl font-semibold text-gray-800">Gestionar Puntos y Premios</h2>
                                <p className="text-sm text-gray-500">Acumular puntos y gestionar canjes de premios</p>
                            </div>
                        </div>
                        <FontAwesomeIcon
                            icon={faArrowRight}
                            className={`text-gray-400 transform transition-transform ${expandedSections.gestionarPuntos ? 'rotate-90' : ''}`}
                        />
                    </button>

                    {expandedSections.gestionarPuntos && (
                        <div className="px-6 pb-6 border-t border-gray-100 bg-gray-50">
                            <div className="pt-6 space-y-6">
                                {/* Acumular Puntos */}
                                <div className="bg-white border border-gray-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faQrcode} className="text-purple-600" />
                                        1. Acumular Puntos Manualmente
                                    </h3>
                                    <div className="space-y-4">
                                        <p className="text-gray-600">
                                            Puedes acumular puntos a un cliente de forma manual cuando realiza una compra sin usar el TPV.
                                        </p>
                                        
                                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-purple-800 mb-2">Cómo acumular puntos:</h4>
                                            <ol className="text-purple-700 text-sm space-y-2 list-decimal list-inside">
                                                <li>En la lista de clientes, haz clic en el botón de <strong>"Acumular Puntos"</strong> del cliente</li>
                                                <li>Ingresa el monto de la compra</li>
                                                <li>Si tienes productos configurados, puedes seleccionar los productos comprados</li>
                                                <li>El sistema calculará automáticamente los puntos según la configuración de tu marca</li>
                                                <li>Confirma la transacción</li>
                                            </ol>
                                        </div>

                                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-green-800 mb-2">✅ Ventajas:</h4>
                                            <ul className="text-green-700 text-sm space-y-1">
                                                <li>• Los puntos se acumulan automáticamente según las reglas de tu marca</li>
                                                <li>• Puedes ver el historial de todas las acumulaciones</li>
                                                <li>• El cliente recibirá una notificación de los puntos acumulados</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>

                                {/* Acumular con QR */}
                                <div className="bg-white border border-gray-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faQrcode} className="text-purple-600" />
                                        2. Acumular Puntos Escaneando QR
                                    </h3>
                                    <div className="space-y-4">
                                        <p className="text-gray-600">
                                            Los clientes pueden mostrar su código QR del ticket y tú puedes escanearlo para acumular puntos rápidamente.
                                        </p>
                                        
                                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-purple-800 mb-2">Cómo usar el escáner QR:</h4>
                                            <ol className="text-purple-700 text-sm space-y-2 list-decimal list-inside">
                                                <li>Accede a la página de "Acumular Puntos" desde el menú o desde una tienda</li>
                                                <li>Haz clic en <strong>"Escanear QR del Cliente"</strong></li>
                                                <li>Permite el acceso a la cámara cuando se solicite</li>
                                                <li>Apunta la cámara hacia el código QR del ticket del cliente</li>
                                                <li>El sistema abrirá automáticamente el formulario con los datos del cliente</li>
                                                <li>Ingresa el monto y confirma la transacción</li>
                                            </ol>
                                        </div>

                                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-yellow-800 mb-2 flex items-center gap-2">
                                                <FontAwesomeIcon icon={faLightbulb} />
                                                Tip
                                            </h4>
                                            <p className="text-yellow-700 text-sm">
                                                El escáner QR es más rápido y reduce errores al no tener que buscar manualmente al cliente. 
                                                Es ideal para tiendas con mucho tráfico.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Canjear Premios */}
                                <div className="bg-white border border-gray-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faGift} className="text-purple-600" />
                                        3. Canjear Premios
                                    </h3>
                                    <div className="space-y-4">
                                        <p className="text-gray-600">
                                            Si tu marca usa el sistema de acumulación con premios, puedes gestionar los canjes de premios de tus clientes.
                                        </p>
                                        
                                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-purple-800 mb-2">Cómo canjear un premio:</h4>
                                            <ol className="text-purple-700 text-sm space-y-2 list-decimal list-inside">
                                                <li>En la lista de clientes, haz clic en <strong>"Canjear Premio"</strong> del cliente</li>
                                                <li>Se mostrará una lista de premios disponibles según los puntos del cliente</li>
                                                <li>Selecciona el premio que el cliente quiere canjear</li>
                                                <li>Confirma el canje</li>
                                                <li>Los puntos se descontarán automáticamente del saldo del cliente</li>
                                            </ol>
                                        </div>

                                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                            <p className="text-yellow-700 text-sm">
                                                <strong>Nota:</strong> Esta funcionalidad solo está disponible para marcas que usan el sistema de "ACUMULACIÓN" 
                                                y tienen premios configurados. Si tu marca usa "PUNTOS", los clientes acumulan puntos pero no hay premios predefinidos.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Editar Cliente */}
                                <div className="bg-white border border-gray-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faEdit} className="text-purple-600" />
                                        4. Editar Información del Cliente
                                    </h3>
                                    <div className="space-y-4">
                                        <p className="text-gray-600">
                                            Puedes actualizar la información de un cliente en cualquier momento.
                                        </p>
                                        
                                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-purple-800 mb-2">Cómo editar:</h4>
                                            <ol className="text-purple-700 text-sm space-y-2 list-decimal list-inside">
                                                <li>En la lista de clientes, haz clic en el icono de <strong>lápiz</strong> del cliente</li>
                                                <li>Se abrirá un modal con todos los datos del cliente</li>
                                                <li>Modifica los campos que necesites actualizar</li>
                                                <li>Guarda los cambios</li>
                                            </ol>
                                        </div>

                                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-green-800 mb-2">Información editable:</h4>
                                            <ul className="text-green-700 text-sm space-y-1">
                                                <li>• Nombre y apellido</li>
                                                <li>• Email y teléfono</li>
                                                <li>• Fecha de cumpleaños</li>
                                                <li>• Género</li>
                                                <li>• Información geográfica</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Sección: Segmentación */}
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-6">
                    <button
                        onClick={() => toggleSection('segmentacion')}
                        className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                        <div className="flex items-center gap-4">
                            <div className="flex-shrink-0 w-10 h-10 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold">
                                <FontAwesomeIcon icon={faChartBar} />
                            </div>
                            <div>
                                <h2 className="text-xl font-semibold text-gray-800">Segmentación de Clientes</h2>
                                <p className="text-sm text-gray-500">Crea segmentos inteligentes para campañas personalizadas</p>
                            </div>
                        </div>
                        <FontAwesomeIcon
                            icon={faArrowRight}
                            className={`text-gray-400 transform transition-transform ${expandedSections.segmentacion ? 'rotate-90' : ''}`}
                        />
                    </button>

                    {expandedSections.segmentacion && (
                        <div className="px-6 pb-6 border-t border-gray-100 bg-gray-50">
                            <div className="pt-6 space-y-6">
                                {/* Segmentación RFM */}
                                <div className="bg-white border border-gray-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faChartBar} className="text-indigo-600" />
                                        1. Segmentación RFM
                                    </h3>
                                    <div className="space-y-4">
                                        <p className="text-gray-600">
                                            La segmentación RFM (Recency, Frequency, Monetary) clasifica a tus clientes según su comportamiento de compra.
                                        </p>
                                        
                                        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-indigo-800 mb-2">Segmentos RFM:</h4>
                                            <ul className="text-indigo-700 text-sm space-y-2">
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Champions:</strong> Clientes que compran frecuentemente, recientemente y gastan mucho
                                                    </div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Loyal Customers:</strong> Clientes leales que compran regularmente
                                                    </div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>At Risk:</strong> Clientes que antes compraban frecuentemente pero han reducido su actividad
                                                    </div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Can't Lose:</strong> Clientes de alto valor que están en riesgo de perderse
                                                    </div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>About to Sleep:</strong> Clientes que están perdiendo interés
                                                    </div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Lost:</strong> Clientes que no han comprado en mucho tiempo
                                                    </div>
                                                </li>
                                            </ul>
                                        </div>

                                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-green-800 mb-2">Cómo usar:</h4>
                                            <ol className="text-green-700 text-sm space-y-2 list-decimal list-inside">
                                                <li>Accede a la página de "Segmentación de Clientes" desde el menú</li>
                                                <li>Selecciona el período de análisis (últimos 3, 6 o 12 meses)</li>
                                                <li>Visualiza los diferentes segmentos y la cantidad de clientes en cada uno</li>
                                                <li>Haz clic en un segmento para ver estrategias recomendadas</li>
                                            </ol>
                                        </div>
                                    </div>
                                </div>

                                {/* Segmentación por Comportamiento */}
                                <div className="bg-white border border-gray-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faChartBar} className="text-indigo-600" />
                                        2. Segmentación por Comportamiento
                                    </h3>
                                    <div className="space-y-4">
                                        <p className="text-gray-600">
                                            Además de RFM, PetitPrime identifica automáticamente segmentos basados en patrones de comportamiento.
                                        </p>
                                        
                                        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-indigo-800 mb-2">Tipos de segmentos de comportamiento:</h4>
                                            <ul className="text-indigo-700 text-sm space-y-2">
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Fin de semana:</strong> Clientes que compran principalmente los fines de semana
                                                    </div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Nocturnos:</strong> Clientes que compran en horarios nocturnos
                                                    </div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Cazapromos:</strong> Clientes que aprovechan principalmente ofertas y promociones
                                                    </div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Ticket alto:</strong> Clientes que realizan compras de alto valor
                                                    </div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Express:</strong> Clientes que hacen compras rápidas y frecuentes
                                                    </div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>
                                                        <strong>Recurrentes:</strong> Clientes que compran con regularidad establecida
                                                    </div>
                                                </li>
                                            </ul>
                                        </div>

                                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-yellow-800 mb-2 flex items-center gap-2">
                                                <FontAwesomeIcon icon={faLightbulb} />
                                                Tip
                                            </h4>
                                            <p className="text-yellow-700 text-sm">
                                                Usa estos segmentos para crear campañas personalizadas. Por ejemplo, envía ofertas especiales 
                                                a los "Cazapromos" o horarios extendidos a los "Nocturnos".
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Estrategias por Segmento */}
                                <div className="bg-white border border-gray-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faChartBar} className="text-indigo-600" />
                                        3. Estrategias Recomendadas por Segmento
                                    </h3>
                                    <div className="space-y-4">
                                        <p className="text-gray-600">
                                            Para cada segmento, PetitPrime te proporciona estrategias recomendadas basadas en las mejores prácticas.
                                        </p>
                                        
                                        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-indigo-800 mb-2">Cómo ver estrategias:</h4>
                                            <ol className="text-indigo-700 text-sm space-y-2 list-decimal list-inside">
                                                <li>En la página de segmentación, haz clic en un segmento específico</li>
                                                <li>Se mostrarán estrategias recomendadas para ese segmento</li>
                                                <li>Cada estrategia incluye acciones concretas que puedes implementar</li>
                                                <li>Usa estas recomendaciones para crear campañas más efectivas</li>
                                            </ol>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Sección: Historial */}
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-6">
                    <button
                        onClick={() => toggleSection('historial')}
                        className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                        <div className="flex items-center gap-4">
                            <div className="flex-shrink-0 w-10 h-10 bg-orange-600 text-white rounded-full flex items-center justify-center font-bold">
                                <FontAwesomeIcon icon={faHistory} />
                            </div>
                            <div>
                                <h2 className="text-xl font-semibold text-gray-800">Historial de Compras</h2>
                                <p className="text-sm text-gray-500">Visualiza el historial completo de un cliente</p>
                            </div>
                        </div>
                        <FontAwesomeIcon
                            icon={faArrowRight}
                            className={`text-gray-400 transform transition-transform ${expandedSections.historial ? 'rotate-90' : ''}`}
                        />
                    </button>

                    {expandedSections.historial && (
                        <div className="px-6 pb-6 border-t border-gray-100 bg-gray-50">
                            <div className="pt-6 space-y-6">
                                {/* Ver Historial */}
                                <div className="bg-white border border-gray-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faHistory} className="text-orange-600" />
                                        1. Ver Historial de un Cliente
                                    </h3>
                                    <div className="space-y-4">
                                        <p className="text-gray-600">
                                            Puedes ver el historial completo de compras y transacciones de cualquier cliente.
                                        </p>
                                        
                                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-orange-800 mb-2">Cómo acceder:</h4>
                                            <ol className="text-orange-700 text-sm space-y-2 list-decimal list-inside">
                                                <li>En la lista de clientes, haz clic en el nombre del cliente o en "Ver Historial"</li>
                                                <li>Se abrirá una página con todo el historial del cliente</li>
                                                <li>Puedes filtrar por ticket específico si el cliente tiene múltiples tarjetas</li>
                                            </ol>
                                        </div>

                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-blue-800 mb-2">Información que verás:</h4>
                                            <ul className="text-blue-700 text-sm space-y-2">
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>Fecha y hora de cada transacción</div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>Monto de cada compra</div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>Puntos acumulados o canjeados</div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>Productos comprados (si están configurados)</div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>Descuentos aplicados</div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>Tienda donde se realizó la compra</div>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>

                                {/* Filtrar por Ticket */}
                                <div className="bg-white border border-gray-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faFilter} className="text-orange-600" />
                                        2. Filtrar por Ticket Específico
                                    </h3>
                                    <div className="space-y-4">
                                        <p className="text-gray-600">
                                            Si un cliente tiene múltiples tarjetas (tickets), puedes filtrar el historial por una tarjeta específica.
                                        </p>
                                        
                                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-orange-800 mb-2">Cómo filtrar:</h4>
                                            <ol className="text-orange-700 text-sm space-y-2 list-decimal list-inside">
                                                <li>En la página de historial, verás un selector de tickets en la parte superior</li>
                                                <li>Selecciona el ticket del que quieres ver el historial</li>
                                                <li>El historial se actualizará automáticamente mostrando solo las transacciones de ese ticket</li>
                                                <li>Puedes cambiar entre tickets en cualquier momento</li>
                                            </ol>
                                        </div>
                                    </div>
                                </div>

                                {/* Ver Detalles de Transacción */}
                                <div className="bg-white border border-gray-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={faInfoCircle} className="text-orange-600" />
                                        3. Ver Detalles de una Transacción
                                    </h3>
                                    <div className="space-y-4">
                                        <p className="text-gray-600">
                                            Puedes ver información detallada de cada transacción individual.
                                        </p>
                                        
                                        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                                            <h4 className="font-semibold text-orange-800 mb-2">Información detallada:</h4>
                                            <ul className="text-orange-700 text-sm space-y-2">
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>Lista completa de productos comprados</div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>Descuentos aplicados y su valor</div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>Monto total antes y después de descuentos</div>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <FontAwesomeIcon icon={faCheckCircle} className="mt-0.5 flex-shrink-0" />
                                                    <div>Puntos ganados o canjeados en esa transacción</div>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Navigation */}
                <div className="flex items-center justify-between pt-6 border-t border-gray-200">
                    <Link
                        to="/documentation/gestion"
                        className="inline-flex items-center gap-2 text-gray-700 hover:text-gray-900 font-medium"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} />
                        Volver a Gestión
                    </Link>
                    <div className="text-sm text-gray-500">
                        <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 mr-2" />
                        Guía completa
                    </div>
                </div>
            </div>
        </DocumentationLayout>
    );
};

export default GestionClientes;

